import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import menuService from "@services/menuService";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import { Checkbox } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import ListCard from "@components_v2/card/list-card";
import { Link } from "react-router-dom";
import { IconMoreHoriz } from "@mzc-cloudplex/icons";
import { Menu } from "@components_v2/menu";
import useCheckPermission from "@hooks/useCheckPermission";
import compareVersions, {
    VERSION_CM_12197_COLLECTION_SHARE_LINK,
    VERSION_CM_12198_COLLECTION_DOWNLOAD,
    VERSION_CM_12290_COLLECTION_ARCHIVE,
} from "@cores/version";
import Typography from "@components_v2/typography/typography";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import { useMemo } from "react";
import { siteMode } from "@cores/siteMode";

const CardCollection = ({
    collection,
    checked,
    thumbnailCrop,
    hiddenCheckbox,
    isCustomMenu,
    draggable,
    onChangeCheck,
    onClickRow,
    onDoubleClickRow,
    onClickDownload,
    onToggleModal,
    onDragStartItem,
    onDragEndItem,
}) => {
    const isCms = siteMode.get() === "CMS";

    const COLLECTIONS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const { propertyPanelTargetId, stage } = useSelector(({ stage, propertyPanel }) => ({
        propertyPanelTargetId: propertyPanel.targetId,
        stage,
    }));
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);
    const onClickCard = (e) => {
        e.stopPropagation();
        if (e?.target?.localName === "input" || isCms) return;
        onClickRow(collection.id);
    };
    const onDoubleClickCard = (e) => {
        e.stopPropagation();
        if (e?.target?.localName === "input") return;
        onDoubleClickRow(collection.id);
    };

    const menuList = useMemo(() => {
        if (isCms) return [];

        return [
            {
                label: t(`common::label::Share link`, `Share link`),
                onClick: () => {
                    onToggleModal("shareLink", true, collection);
                },
                clickClose: true,
                hidden: compareVersions(stage.version, VERSION_CM_12197_COLLECTION_SHARE_LINK) < 0,
                disabled: !SHARES_PERMISSIONS.EXTERNAL,
            },
            {
                label: t(`common::label::Download`, `Download`),
                onClick: () => {
                    onClickDownload(collection);
                },
                clickClose: true,
                hidden: compareVersions(stage.version, VERSION_CM_12198_COLLECTION_DOWNLOAD) < 0,
                disabled: !DOWNLOAD_PERMISSIONS.DOWNLOAD,
                divider: true,
            },
            {
                label: t(`common::label::Delete`, `Delete`),
                onClick: () => {
                    onToggleModal("delete", true, collection);
                },
                clickClose: true,
                disabled: !COLLECTIONS_PERMISSIONS.EDIT,
                divider: true,
            },
            {
                label: t(`common::label::Archive`, `Archive`),
                onClick: () => {
                    onToggleModal("archive", true, collection);
                },
                clickClose: true,
                hidden: compareVersions(stage.version, VERSION_CM_12290_COLLECTION_ARCHIVE) < 0,
                disabled: !COLLECTIONS_PERMISSIONS.ARCHIVE,
                divider: true,
            },
        ];
    }, [isCms, onClickDownload]);

    const collectionDetailPath = useMemo(() => {
        if (isCustomMenu) {
            return `${pathname.split("/search")[0]}/${collection.id}`; //Note: 컬렉션 검색 페이지에서 상세로 넘어갈 경우 /search를 제거한 후 id를 붙여야 함
        }

        return isCms
            ? menuService.paths.library.assetsCollectionDetail(spaceId, projectId, collection.id)
            : menuService.paths.assetsCollectionDetail(spaceId, projectId, collection.id);
    }, [isCms, isCustomMenu]);

    return (
        <ListCard
            width={202}
            thumbnail={
                <CollectionThumbnail
                    collection={collection}
                    size={"medium"}
                    variant={"card"}
                    selected={checked}
                    thumbnailCrop={thumbnailCrop}
                />
            }
            checkbox={
                <Checkbox
                    color={"greySecondary"}
                    checked={checked ?? false}
                    onChange={(e) => {
                        e.stopPropagation();
                        onChangeCheck(collection);
                    }}
                    hidden={hiddenCheckbox}
                />
            }
            name={{
                name: (
                    <Link
                        style={{
                            color: theme.palette.secondary[800],
                            fontWeight: 500,
                            display: `-webkit-box`,
                            WebkitLineClamp: `2`,
                            textOverflow: `ellipsis`,
                            WebkitBoxOrient: `vertical`,
                            maxHeight: 45,
                            overflow: `hidden`,
                        }}
                        to={collectionDetailPath}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Typography variant={"body1_500"} color={theme.palette.greySecondary.main}>
                            {collection.name}
                        </Typography>
                    </Link>
                ),
                badge: <BadgeCollection totalCount={collection.assetTotalCount} />,
            }}
            actions={
                <Menu
                    menuWidth={160}
                    placement={"right"}
                    type={"icon"}
                    label={<IconMoreHoriz size={16} />}
                    color={"inherit"}
                    size={"small"}
                    list={menuList}
                    useInCard={true}
                />
            }
            onClickCard={onClickCard}
            onDoubleClickCard={onDoubleClickCard}
            draggable={draggable}
            onDragStart={(e) => onDragStartItem?.(e, collection)}
            onDragEnd={onDragEndItem}
        />
    );
};
CardCollection.propTypes = {};

export default CardCollection;
